import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  CheckCircleRounded,
  CloseRounded,
  EditRounded,
  FactCheckRounded,
  HourglassBottomRounded,
  SaveRounded,
  WorkspacesRounded,
} from '@mui/icons-material';
import {
  cancelIndustrialWorkflowWorkReport,
  completeIndustrialWorkflowWorkReport,
  createIndustrialWorkflowWorkReport,
  listIndustrialWorkflowWorkAreas,
  listIndustrialWorkflowWorkReportDefinitions,
  listIndustrialWorkflowWorkReports,
  updateIndustrialWorkflowWorkReport,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowCase,
  IndustrialWorkflowFieldType,
  IndustrialWorkflowWorkArea,
  IndustrialWorkflowWorkReport,
  IndustrialWorkflowWorkReportDefinition,
  IndustrialWorkflowWorkReportValue,
} from '../types/industrialWorkflows';

type Props = {
  workflowCase: IndustrialWorkflowCase | null;
  canManageWorkReports: boolean;
  operableWorkReportIds?: string[];
  hiddenWorkReportIds?: string[];
  requestedWorkReportId?: string | null;
  onRequestedWorkReportConsumed?: () => void;
};

type WorkReportValueForm = Record<string, unknown>;

const WORK_REPORT_DIALOG_BACKDROP_PROPS = {
  sx: {
    bgcolor: 'rgba(2,6,23,0.78)',
    backdropFilter: 'blur(6px)',
  },
};

const WORK_REPORT_DIALOG_PAPER_PROPS = {
  sx: {
    borderRadius: 4,
    border: `1px solid ${alpha('#38bdf8', 0.22)}`,
    bgcolor: '#0f1b2f',
    backgroundImage: 'none',
    boxShadow: '0 28px 80px rgba(0,0,0,0.48)',
    overflow: 'hidden',
  },
};

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function isEmptyValue(value: unknown) {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return !value.trim();
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
}

function valueToTextInput(value: unknown) {
  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    return value.join('\n');
  }

  return String(value);
}

function toDateTimeLocalValue(value: unknown) {
  if (typeof value !== 'string' || !value) {
    return '';
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value.slice(0, 16);
  }

  const timezoneOffset =
    parsedDate.getTimezoneOffset() * 60 * 1000;

  return new Date(
    parsedDate.getTime() - timezoneOffset,
  )
    .toISOString()
    .slice(0, 16);
}

function formatWorkReportValue(value: unknown) {
  if (isEmptyValue(value)) {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
}

function getFieldTypeLabel(fieldType: IndustrialWorkflowFieldType) {
  if (fieldType === 'long_text') return 'Texto largo';
  if (fieldType === 'integer') return 'Número entero';
  if (fieldType === 'number') return 'Número decimal';
  if (fieldType === 'currency') return 'Importe';
  if (fieldType === 'boolean') return 'Sí / no';
  if (fieldType === 'date') return 'Fecha';
  if (fieldType === 'datetime') return 'Fecha y hora';
  if (fieldType === 'select') return 'Lista';
  if (fieldType === 'multi_select') return 'Selección múltiple';
  if (fieldType === 'user') return 'Usuario';
  if (fieldType === 'team') return 'Equipo';
  if (fieldType === 'email') return 'Correo';
  if (fieldType === 'url') return 'Enlace';

  return 'Texto';
}

function getReportStatusVisual(
  status: IndustrialWorkflowWorkReport['status'],
) {
  if (status === 'completed') {
    return {
      label: 'Completado',
      color: '#4ade80',
      bgcolor: alpha('#22c55e', 0.15),
      icon: <CheckCircleRounded fontSize="small" />,
    };
  }

  if (status === 'cancelled') {
    return {
      label: 'Cancelado',
      color: '#94a3b8',
      bgcolor: alpha('#64748b', 0.14),
      icon: <CloseRounded fontSize="small" />,
    };
  }

  return {
    label: 'Pendiente',
    color: '#fbbf24',
    bgcolor: alpha('#f59e0b', 0.16),
    icon: <HourglassBottomRounded fontSize="small" />,
  };
}

function getWorkAreaLabel(
  workAreaById: Map<string, IndustrialWorkflowWorkArea>,
  workAreaId: string | null,
) {
  if (!workAreaId) {
    return null;
  }

  return workAreaById.get(workAreaId)?.name ?? 'Área no disponible';
}

function buildValueForm(
  report: IndustrialWorkflowWorkReport | null,
): WorkReportValueForm {
  if (!report) {
    return {};
  }

  return Object.fromEntries(
    report.values.map((fieldValue) => [
      fieldValue.code,
      fieldValue.value ?? null,
    ]),
  );
}

function getCaseFieldWriteMode(
  settings: Record<string, unknown> | null | undefined,
) {
  return settings?.case_field_write_mode === 'on_complete'
    ? 'on_complete'
    : 'read_only';
}

function isEditableWorkReportValue(
  fieldValue: IndustrialWorkflowWorkReportValue,
) {
  if (
    !fieldValue.is_active
    || fieldValue.is_readonly
  ) {
    return false;
  }

  if (fieldValue.field_source === 'work_report_field') {
    return true;
  }

  return (
    fieldValue.field_source === 'case_field'
    && getCaseFieldWriteMode(fieldValue.settings) === 'on_complete'
  );
}

function buildSubmitValues(
  report: IndustrialWorkflowWorkReport,
  valueForm: WorkReportValueForm,
) {
  return Object.fromEntries(
    report.values
      .filter(isEditableWorkReportValue)
      .map((fieldValue) => [
        fieldValue.code,
        Object.prototype.hasOwnProperty.call(
          valueForm,
          fieldValue.code,
        )
          ? valueForm[fieldValue.code]
          : null,
      ]),
  );
}

function WorkReportFieldInput({
  field,
  value,
  disabled,
  onChange,
}: {
  field: IndustrialWorkflowWorkReportValue;
  value: unknown;
  disabled: boolean;
  onChange: (value: unknown) => void;
}) {
  const isWritableCaseField =
    field.field_source === 'case_field'
    && getCaseFieldWriteMode(field.settings) === 'on_complete';

  const isReadonly =
    disabled
    || !isEditableWorkReportValue(field);

  const helperText = [
    field.description,
    field.field_source === 'case_field'
      ? isWritableCaseField
        ? 'Dato del expediente editable desde este parte. Se actualizará al completar.'
        : 'Dato tomado del expediente.'
      : null,
    field.is_required_to_complete
      ? 'Obligatorio para completar.'
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (field.field_type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            disabled={isReadonly}
            onChange={(event) =>
              onChange(event.target.checked)
            }
          />
        }
        label={field.label}
      />
    );
  }

  if (field.field_type === 'select') {
    return (
      <TextField
        select
        label={field.label}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) =>
          onChange(event.target.value)
        }
        fullWidth
        required={field.is_required_to_complete}
        disabled={isReadonly}
        helperText={helperText || undefined}
      >
        {(field.options ?? []).map((option) => (
          <MenuItem
            key={String(option)}
            value={String(option)}
          >
            {String(option)}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (field.field_type === 'multi_select') {
    return (
      <TextField
        label={field.label}
        value={valueToTextInput(value)}
        onChange={(event) =>
          onChange(
            event.target.value
              .split('\n')
              .map((item) => item.trim())
              .filter(Boolean),
          )
        }
        multiline
        minRows={3}
        fullWidth
        required={field.is_required_to_complete}
        disabled={isReadonly}
        helperText={
          helperText
          || 'Introduce un valor por línea.'
        }
      />
    );
  }

  const inputType =
    field.field_type === 'integer'
    || field.field_type === 'number'
    || field.field_type === 'currency'
      ? 'number'
      : field.field_type === 'date'
        ? 'date'
        : field.field_type === 'datetime'
          ? 'datetime-local'
          : field.field_type === 'email'
            ? 'email'
            : field.field_type === 'url'
              ? 'url'
              : 'text';

  const inputValue =
    field.field_type === 'datetime'
      ? toDateTimeLocalValue(value)
      : field.field_type === 'date'
        ? valueToTextInput(value).slice(0, 10)
        : valueToTextInput(value);

  return (
    <TextField
      label={field.label}
      type={inputType}
      value={inputValue}
      onChange={(event) => {
        const nextValue = event.target.value;

        if (
          field.field_type === 'integer'
          || field.field_type === 'number'
          || field.field_type === 'currency'
        ) {
          onChange(
            nextValue === ''
              ? null
              : Number(nextValue),
          );

          return;
        }

        onChange(nextValue || null);
      }}
      multiline={field.field_type === 'long_text'}
      minRows={field.field_type === 'long_text' ? 4 : undefined}
      fullWidth
      required={field.is_required_to_complete}
      disabled={isReadonly}
      helperText={helperText || undefined}
      InputLabelProps={
        inputType === 'date'
        || inputType === 'datetime-local'
          ? {
              shrink: true,
            }
          : undefined
      }
    />
  );
}

export default function IndustrialWorkflowCaseWorkReportsPanel({
  workflowCase,
  canManageWorkReports,
  operableWorkReportIds = [],
  hiddenWorkReportIds = [],
  requestedWorkReportId = null,
  onRequestedWorkReportConsumed,
}: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [
    selectedReport,
    setSelectedReport,
  ] =
    useState<IndustrialWorkflowWorkReport | null>(
      null,
    );

  const [
    reportDialogOpen,
    setReportDialogOpen,
  ] = useState(false);

  const [
    manualDialogOpen,
    setManualDialogOpen,
  ] = useState(false);

  const [
    manualDefinitionId,
    setManualDefinitionId,
  ] = useState('');

  const [
    valueForm,
    setValueForm,
  ] = useState<WorkReportValueForm>({});

  const [
    completedEditMode,
    setCompletedEditMode,
  ] = useState(false);

  const [
    completedEditReason,
    setCompletedEditReason,
  ] = useState('');

  const reportsQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-reports',
      workflowCase?.id,
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkReports(
        workflowCase?.id as string,
        true,
      ),
    enabled: Boolean(workflowCase?.id),
    retry: false,
    refetchOnMount: 'always',
  });

  const definitionsQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-report-definitions',
      workflowCase?.process_definition_id,
      workflowCase?.current_stage_definition_id,
      false,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkReportDefinitions(
        workflowCase?.process_definition_id as string,
        workflowCase?.current_stage_definition_id as string,
        false,
      ),
    enabled: Boolean(
      workflowCase?.process_definition_id
      && workflowCase?.current_stage_definition_id
      && canManageWorkReports,
    ),
    retry: false,
  });

  const workAreasQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-areas',
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkAreas(true),
    enabled: Boolean(workflowCase?.id),
    retry: false,
  });

  const reports = reportsQuery.data ?? [];

  const hiddenWorkReportIdSet = useMemo(
    () => new Set(hiddenWorkReportIds),
    [hiddenWorkReportIds],
  );

  const operableWorkReportIdSet = useMemo(
    () => new Set(operableWorkReportIds),
    [operableWorkReportIds],
  );

  const visibleReports = useMemo(
    () =>
      reports.filter(
        (report) =>
          !hiddenWorkReportIdSet.has(report.id),
      ),
    [reports, hiddenWorkReportIdSet],
  );

  const hiddenLinkedReportsCount =
    reports.length - visibleReports.length;

  const workAreaById = useMemo(
    () =>
      new Map(
        (workAreasQuery.data ?? []).map((workArea) => [
          workArea.id,
          workArea,
        ]),
      ),
    [workAreasQuery.data],
  );

  const reportDefinitionIdsInCurrentEntry = useMemo(
    () =>
      new Set(
        reports.map(
          (report) =>
            report.work_report_definition_id,
        ),
      ),
    [reports],
  );

  const availableDefinitionsToCreate = useMemo(
    () =>
      (definitionsQuery.data ?? [])
        .filter(
          (definition) =>
            definition.is_active
            && !reportDefinitionIdsInCurrentEntry.has(
              definition.id,
            ),
        )
        .sort(
          (left, right) =>
            left.display_order - right.display_order
            || left.name.localeCompare(right.name),
        ),
    [
      definitionsQuery.data,
      reportDefinitionIdsInCurrentEntry,
    ],
  );

  const pendingRequiredReports = useMemo(
    () =>
      visibleReports.filter(
        (report) =>
          report.definition
            ?.is_required_to_complete_stage
          && report.status !== 'completed',
      ),
    [visibleReports],
  );

  const completedReports = useMemo(
    () =>
      visibleReports.filter(
        (report) => report.status === 'completed',
      ).length,
    [visibleReports],
  );

  const showCompactEmptyState =
    !reportsQuery.isLoading
    && !reportsQuery.isError
    && !visibleReports.length
    && !pendingRequiredReports.length;

  const selectedReportStatusVisual =
    selectedReport
      ? getReportStatusVisual(selectedReport.status)
      : null;

  const selectedReportIsDraft =
    selectedReport?.status === 'draft';

  const selectedReportIsCompleted =
    selectedReport?.status === 'completed';

  const selectedReportCanBeOperated =
    Boolean(
      selectedReport
      && operableWorkReportIdSet.has(selectedReport.id),
    );

  const selectedReportCanBeEdited =
    Boolean(
      selectedReport
      && (
        canManageWorkReports
          ? (
              selectedReportIsDraft
              || (
                selectedReportIsCompleted
                && completedEditMode
              )
            )
          : (
              selectedReportCanBeOperated
              && selectedReportIsDraft
            )
      ),
    );

  const selectedReportMissingRequiredFields =
    selectedReport
      ? selectedReport.values.filter(
          (fieldValue) =>
            isEditableWorkReportValue(fieldValue)
            && fieldValue.is_required_to_complete
            && isEmptyValue(valueForm[fieldValue.code]),
        )
      : [];

  const selectedReportHasMissingRequiredFields =
    selectedReportMissingRequiredFields.length > 0;

  useEffect(
    () => {
      setSelectedReport(null);
      setReportDialogOpen(false);
      setManualDialogOpen(false);
      setManualDefinitionId('');
      setValueForm({});
      setCompletedEditMode(false);
      setCompletedEditReason('');
    },
    [workflowCase?.id],
  );

  useEffect(
    () => {
      setValueForm(buildValueForm(selectedReport));
    },
    [selectedReport],
  );

  useEffect(
    () => {
      if (!requestedWorkReportId) {
        return;
      }

      const requestedReport = reports.find(
        (report) =>
          report.id === requestedWorkReportId,
      );

      if (!requestedReport) {
        if (!reportsQuery.isLoading) {
          onRequestedWorkReportConsumed?.();
        }

        return;
      }

      setSelectedReport(requestedReport);
      setReportDialogOpen(true);
      onRequestedWorkReportConsumed?.();
    },
    [
      requestedWorkReportId,
      reports,
      reportsQuery.isLoading,
      onRequestedWorkReportConsumed,
    ],
  );

  const invalidateReports = async () => {
    if (!workflowCase?.id) {
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-work-reports',
          workflowCase.id,
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-case-events',
          workflowCase.id,
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-cases',
        ],
      }),
    ]);
  };

  const createReportMutation = useMutation({
    mutationFn: (workReportDefinitionId: string) =>
      createIndustrialWorkflowWorkReport(
        workflowCase?.id as string,
        {
          work_report_definition_id:
            workReportDefinitionId,
          previous_work_report_id: null,
          continuity_key: null,
          values: {},
        },
      ),
    onSuccess: async (createdReport) => {
      await invalidateReports();

      setManualDialogOpen(false);
      setManualDefinitionId('');
      setSelectedReport(createdReport);
      setReportDialogOpen(true);

      enqueueSnackbar(
        'Parte de trabajo creado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const updateReportMutation = useMutation({
    mutationFn: ({
      report,
      values,
      changeReason,
    }: {
      report: IndustrialWorkflowWorkReport;
      values: WorkReportValueForm;
      changeReason?: string | null;
    }) =>
      updateIndustrialWorkflowWorkReport(
        report.case_id,
        report.id,
        {
          values: buildSubmitValues(
            report,
            values,
          ),
          change_reason: changeReason ?? null,
        },
      ),
    onSuccess: async (updatedReport) => {
      await invalidateReports();

      setSelectedReport(updatedReport);

      enqueueSnackbar(
        'Borrador del parte guardado.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const completeReportMutation = useMutation({
    mutationFn: ({
      report,
      values,
    }: {
      report: IndustrialWorkflowWorkReport;
      values: WorkReportValueForm;
    }) =>
      completeIndustrialWorkflowWorkReport(
        report.case_id,
        report.id,
        {
          values: buildSubmitValues(
            report,
            values,
          ),
        },
      ),
    onSuccess: async (updatedReport) => {
      await invalidateReports();

      setSelectedReport(updatedReport);
      setReportDialogOpen(false);

      enqueueSnackbar(
        'Parte de trabajo completado.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const cancelReportMutation = useMutation({
    mutationFn: (report: IndustrialWorkflowWorkReport) =>
      cancelIndustrialWorkflowWorkReport(
        report.case_id,
        report.id,
        {
          reason: null,
        },
      ),
    onSuccess: async (updatedReport) => {
      await invalidateReports();

      setSelectedReport(updatedReport);
      setReportDialogOpen(false);

      enqueueSnackbar(
        'Parte de trabajo cancelado.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const reportMutationIsPending =
    updateReportMutation.isPending
    || completeReportMutation.isPending
    || cancelReportMutation.isPending;

  const handleOpenReport = (
    report: IndustrialWorkflowWorkReport,
  ) => {
    setSelectedReport(report);
    setCompletedEditMode(false);
    setCompletedEditReason('');
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    if (reportMutationIsPending) {
      return;
    }

    setReportDialogOpen(false);
    setSelectedReport(null);
    setValueForm({});
    setCompletedEditMode(false);
    setCompletedEditReason('');
  };

  const handleSaveReport = () => {
    if (!selectedReport) {
      return;
    }

    if (
      selectedReport.status === 'completed'
      && !completedEditReason.trim()
    ) {
      enqueueSnackbar(
        'Indica el motivo de modificación del parte completado.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    updateReportMutation.mutate({
      report: selectedReport,
      values: valueForm,
      changeReason:
        selectedReport.status === 'completed'
          ? completedEditReason.trim()
          : null,
    });
  };

  const handleCompleteReport = () => {
    if (!selectedReport) {
      return;
    }

    if (selectedReportHasMissingRequiredFields) {
      enqueueSnackbar(
        'Completa los campos obligatorios antes de cerrar el parte.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    completeReportMutation.mutate({
      report: selectedReport,
      values: valueForm,
    });
  };

  const handleCancelReport = () => {
    if (!selectedReport) {
      return;
    }

    cancelReportMutation.mutate(selectedReport);
  };

  const handleCreateManualReport = () => {
    if (!manualDefinitionId) {
      enqueueSnackbar(
        'Selecciona el parte que quieres crear.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    createReportMutation.mutate(manualDefinitionId);
  };

  const renderReportCard = (
    report: IndustrialWorkflowWorkReport,
  ) => {
    const statusVisual =
      getReportStatusVisual(report.status);

    const definition =
      report.definition;

    const reportTitle =
      definition?.name
      ?? 'Parte de trabajo';

    const workAreaLabel =
      getWorkAreaLabel(
        workAreaById,
        report.work_area_id,
      );

    const previewValues =
      report.values.slice(0, 4);

    return (
      <Paper
        key={report.id}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(
            definition?.is_required_to_complete_stage
              && report.status !== 'completed'
              ? '#f59e0b'
              : '#38bdf8',
            0.22,
          )}`,
          bgcolor: 'rgba(2,6,23,0.28)',
          px: 1.25,
          py: 1.1,
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
            spacing={1}
          >
            <Stack
              direction="row"
              spacing={0.85}
              alignItems="flex-start"
            >
              <FactCheckRounded
                sx={{
                  mt: 0.1,
                  color:
                    report.status === 'completed'
                      ? '#4ade80'
                      : '#fbbf24',
                }}
              />

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                  }}
                >
                  {reportTitle}
                </Typography>

                {definition?.description ? (
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.8rem',
                      mt: 0.2,
                    }}
                  >
                    {definition.description}
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.6}
              flexWrap="wrap"
              useFlexGap
              justifyContent="flex-end"
            >
              <Chip
                size="small"
                icon={statusVisual.icon}
                label={statusVisual.label}
                sx={{
                  color: statusVisual.color,
                  bgcolor: statusVisual.bgcolor,
                  fontWeight: 850,
                }}
              />

              {definition?.is_required_to_complete_stage ? (
                <Chip
                  size="small"
                  label="Obligatorio"
                  sx={{
                    color: '#fde68a',
                    bgcolor: alpha('#f59e0b', 0.15),
                    fontWeight: 850,
                  }}
                />
              ) : null}

              {workAreaLabel ? (
                <Chip
                  size="small"
                  icon={<WorkspacesRounded />}
                  label={workAreaLabel}
                  sx={{
                    color: '#bae6fd',
                    bgcolor: alpha('#0284c7', 0.16),
                  }}
                />
              ) : null}
            </Stack>
          </Stack>

          {previewValues.length ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 0.75,
              }}
            >
              {previewValues.map((fieldValue) => (
                <Box
                  key={fieldValue.field_definition_id}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(
                      '#94a3b8',
                      0.12,
                    )}`,
                    bgcolor: 'rgba(15,23,42,0.32)',
                    px: 0.95,
                    py: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.68rem',
                      fontWeight: 900,
                      textTransform: 'uppercase',
                      letterSpacing: 0.55,
                    }}
                  >
                    {fieldValue.label}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#e2e8f0',
                      fontSize: '0.83rem',
                      mt: 0.15,
                    }}
                  >
                    {formatWorkReportValue(fieldValue.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.82rem',
              }}
            >
              Este parte todavía no tiene campos configurados.
            </Typography>
          )}

          <Stack
            direction="row"
            justifyContent="flex-end"
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditRounded />}
              onClick={() => handleOpenReport(report)}
              sx={{
                textTransform: 'none',
                fontWeight: 850,
              }}
            >
              Abrir parte
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  if (!workflowCase) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha('#38bdf8', 0.18)}`,
        bgcolor: showCompactEmptyState
          ? 'rgba(15,23,42,0.28)'
          : 'rgba(15,23,42,0.48)',
        px: 1.35,
        py: showCompactEmptyState ? 0.85 : 1.35,
      }}
    >
      <Stack spacing={showCompactEmptyState ? 0 : 1.25}>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            md: 'center',
          }}
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={0.85}
            alignItems="center"
          >
            <FactCheckRounded
              sx={{
                color: '#c4b5fd',
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                Partes independientes
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                }}
              >
                {showCompactEmptyState
                  ? hiddenLinkedReportsCount > 0
                    ? 'Los partes asociados se gestionan desde las tareas.'
                    : 'Sin partes independientes en esta entrada.'
                  : 'Registros estructurados no vinculados a tareas. Los partes asociados se gestionan dentro de su tarea.'}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.65}
            flexWrap="wrap"
            useFlexGap
            justifyContent="flex-end"
          >
            <Chip
              size="small"
              label={
                showCompactEmptyState
                  ? 'Sin partes independientes'
                  : `${visibleReports.length} parte${
                      visibleReports.length === 1 ? '' : 's'
                    } independiente${
                      visibleReports.length === 1 ? '' : 's'
                    }`
              }
              sx={{
                color: '#cbd5e1',
                bgcolor: alpha('#64748b', 0.12),
              }}
            />

            {!showCompactEmptyState ? (
              <Chip
                size="small"
                label={`${completedReports} completado${
                  completedReports === 1 ? '' : 's'
                }`}
                sx={{
                  color: '#bbf7d0',
                  bgcolor: alpha('#22c55e', 0.15),
                }}
              />
            ) : null}

            {pendingRequiredReports.length ? (
              <Chip
                size="small"
                label={`${pendingRequiredReports.length} obligatorio${
                  pendingRequiredReports.length === 1
                    ? ''
                    : 's'
                } pendiente${
                  pendingRequiredReports.length === 1
                    ? ''
                    : 's'
                }`}
                sx={{
                  color: '#fde68a',
                  bgcolor: alpha('#f59e0b', 0.16),
                  fontWeight: 900,
                }}
              />
            ) : null}

            {canManageWorkReports ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRounded />}
                onClick={() => setManualDialogOpen(true)}
                disabled={
                  definitionsQuery.isLoading
                  || !availableDefinitionsToCreate.length
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 850,
                }}
              >
                Crear parte independiente
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {reportsQuery.isFetching ? (
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
            sx={{ color: '#94a3b8' }}
          >
            <CircularProgress size={17} />

            <Typography sx={{ fontSize: '0.82rem' }}>
              Cargando partes...
            </Typography>
          </Stack>
        ) : null}

        {reportsQuery.isError ? (
          <Alert severity="error" variant="outlined">
            {getErrorMessage(reportsQuery.error)}
          </Alert>
        ) : null}

        {!showCompactEmptyState
        && !reportsQuery.isLoading
        && !reportsQuery.isError
        && !visibleReports.length ? (
          <Alert severity="info" variant="outlined">
            {hiddenLinkedReportsCount > 0
              ? 'Los partes asociados a tareas se gestionan desde su tarjeta de trabajo.'
              : 'No hay partes independientes para la entrada actual de etapa.'}
          </Alert>
        ) : null}

        {pendingRequiredReports.length ? (
          <Alert severity="warning" variant="outlined">
            Hay partes independientes obligatorios pendientes. El backend
            no permitirá finalizar la etapa hasta completarlos.
          </Alert>
        ) : null}

        {visibleReports.length ? (
          <Stack spacing={0.85}>
            {visibleReports.map(renderReportCard)}
          </Stack>
        ) : null}
      </Stack>

      <Dialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        fullWidth
        maxWidth="md"
        BackdropProps={WORK_REPORT_DIALOG_BACKDROP_PROPS}
        PaperProps={WORK_REPORT_DIALOG_PAPER_PROPS}
      >
        <DialogTitle>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                {selectedReport?.definition?.name
                  ?? 'Parte de trabajo'}
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                  mt: 0.25,
                }}
              >
                {selectedReport?.definition?.description
                  ?? 'Completa los datos requeridos para esta etapa.'}
              </Typography>
            </Box>

            {selectedReportStatusVisual ? (
              <Chip
                size="small"
                icon={selectedReportStatusVisual.icon}
                label={selectedReportStatusVisual.label}
                sx={{
                  alignSelf: {
                    xs: 'flex-start',
                    sm: 'center',
                  },
                  color: selectedReportStatusVisual.color,
                  bgcolor: selectedReportStatusVisual.bgcolor,
                  fontWeight: 900,
                }}
              />
            ) : null}
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {selectedReport?.definition
              ?.is_required_to_complete_stage ? (
              <Alert severity="warning" variant="outlined">
                Este parte es obligatorio para avanzar de etapa.
              </Alert>
            ) : null}

            {selectedReport?.status === 'completed'
            && !completedEditMode ? (
              <Alert severity="success" variant="outlined">
                Este parte está completado. Puedes consultarlo o
                modificarlo indicando un motivo, dejando trazabilidad
                en la actividad del expediente.
              </Alert>
            ) : null}

            {selectedReport?.status === 'completed'
            && completedEditMode ? (
              <TextField
                label="Motivo de modificación"
                value={completedEditReason}
                onChange={(event) =>
                  setCompletedEditReason(event.target.value)
                }
                fullWidth
                required
                multiline
                minRows={2}
                helperText="Este motivo quedará registrado en la actividad del expediente."
              />
            ) : null}

            {selectedReportHasMissingRequiredFields
            && selectedReportCanBeEdited ? (
              <Alert severity="info" variant="outlined">
                Campos obligatorios pendientes:{' '}
                {selectedReportMissingRequiredFields
                  .map((fieldValue) => fieldValue.label)
                  .join(', ')}
              </Alert>
            ) : null}

            {selectedReport?.values.length ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 1.2,
                }}
              >
                {selectedReport.values
                  .filter((fieldValue) => fieldValue.is_active)
                  .sort(
                    (left, right) =>
                      left.display_order - right.display_order
                      || left.label.localeCompare(right.label),
                  )
                  .map((fieldValue) => (
                    <Box
                      key={fieldValue.field_definition_id}
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <WorkReportFieldInput
                        field={fieldValue}
                        value={valueForm[fieldValue.code]}
                        disabled={!selectedReportCanBeEdited}
                        onChange={(nextValue) =>
                          setValueForm((current) => ({
                            ...current,
                            [fieldValue.code]: nextValue,
                          }))
                        }
                      />

                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.68rem',
                          mt: 0.35,
                        }}
                      >
                        {getFieldTypeLabel(fieldValue.field_type)}
                        {fieldValue.field_source === 'case_field'
                        && getCaseFieldWriteMode(fieldValue.settings)
                          === 'on_complete'
                          ? ' · actualiza expediente'
                          : ''}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ) : (
              <Alert severity="info" variant="outlined">
                Este parte no tiene campos configurados.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          {canManageWorkReports
          && selectedReportIsDraft
          && selectedReportCanBeEdited ? (
            <Button
              color="error"
              startIcon={<CloseRounded />}
              disabled={reportMutationIsPending}
              onClick={handleCancelReport}
            >
              Cancelar parte
            </Button>
          ) : null}

          <Box sx={{ flex: 1 }} />

          {selectedReportIsCompleted
          && canManageWorkReports
          && !completedEditMode ? (
            <Button
              variant="outlined"
              startIcon={<EditRounded />}
              disabled={reportMutationIsPending}
              onClick={() => {
                setCompletedEditMode(true);
                setCompletedEditReason('');
              }}
            >
              Modificar con motivo
            </Button>
          ) : null}

          <Button
            onClick={handleCloseReportDialog}
            disabled={reportMutationIsPending}
          >
            Cerrar
          </Button>

          {selectedReportCanBeEdited ? (
            <Button
              variant="outlined"
              startIcon={<SaveRounded />}
              onClick={handleSaveReport}
              disabled={
                reportMutationIsPending
                || (
                  selectedReportIsCompleted
                  && !completedEditReason.trim()
                )
              }
            >
              {selectedReportIsCompleted
                ? 'Guardar modificación'
                : 'Guardar borrador'}
            </Button>
          ) : null}

          {selectedReportIsDraft
          && selectedReportCanBeEdited ? (
            <Button
              variant="contained"
              startIcon={<CheckCircleRounded />}
              onClick={handleCompleteReport}
              disabled={
                reportMutationIsPending
                || selectedReportHasMissingRequiredFields
              }
            >
              Completar parte
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={manualDialogOpen}
        onClose={() => {
          if (!createReportMutation.isPending) {
            setManualDialogOpen(false);
            setManualDefinitionId('');
          }
        }}
        fullWidth
        maxWidth="sm"
        BackdropProps={WORK_REPORT_DIALOG_BACKDROP_PROPS}
        PaperProps={WORK_REPORT_DIALOG_PAPER_PROPS}
      >
        <DialogTitle>Crear parte de trabajo</DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {availableDefinitionsToCreate.length ? (
              <TextField
                select
                label="Parte disponible"
                value={manualDefinitionId}
                onChange={(event) =>
                  setManualDefinitionId(event.target.value)
                }
                fullWidth
                required
              >
                {availableDefinitionsToCreate.map(
                  (
                    definition:
                      IndustrialWorkflowWorkReportDefinition,
                  ) => (
                    <MenuItem
                      key={definition.id}
                      value={definition.id}
                    >
                      {definition.name}
                    </MenuItem>
                  ),
                )}
              </TextField>
            ) : (
              <Alert severity="info" variant="outlined">
                No hay partes configurados disponibles para crear
                manualmente en esta etapa.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setManualDialogOpen(false);
              setManualDefinitionId('');
            }}
            disabled={createReportMutation.isPending}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleCreateManualReport}
            disabled={
              createReportMutation.isPending
              || !manualDefinitionId
            }
          >
            Crear parte
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}